import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getEnvelopeAutoReminders, updateEnvelopeAutoReminders } from './envelope-api';
import {
  type QueryOptionsType,
  type MutationOptionsType,
  type OptimisticUpdateContext,
  optimisticOnMutate,
} from './query-helpers';

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const getEnvelopeAutoRemindersQueryKey = (envelopeId: string | undefined) => [
  'get-envelope-auto-reminders',
  String(envelopeId),
];

export function useGetEnvelopeAutoReminders(
  envelopeId: string | undefined,
  options?: QueryOptionsType<typeof getEnvelopeAutoReminders>
) {
  return useQuery(
    getEnvelopeAutoRemindersQueryKey(envelopeId),
    () => getEnvelopeAutoReminders({ envelopeId: envelopeId! }),
    {
      ...options,
      enabled: options?.enabled && !!envelopeId,
    }
  );
}

export function useUpdateEnvelopeAutoReminders(
  envelopeId: string,
  options?: MutationOptionsType<
    typeof updateEnvelopeAutoReminders,
    Omit<Parameters<typeof updateEnvelopeAutoReminders>[0], 'envelopeId'>
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    Awaited<ReturnType<typeof updateEnvelopeAutoReminders>>,
    unknown,
    { automatedRemindersEnabled: boolean },
    OptimisticUpdateContext
  >({
    ...options,
    mutationFn: ({ automatedRemindersEnabled }) =>
      updateEnvelopeAutoReminders({ envelopeId, automatedRemindersEnabled }),
    async onMutate(variables) {
      const { automatedRemindersEnabled } = variables;
      const queryKey = getEnvelopeAutoRemindersQueryKey(envelopeId);
      return optimisticOnMutate<typeof getEnvelopeAutoReminders>(queryClient, queryKey, automatedRemindersEnabled);
    },
    onError: (err, variables, context) => {
      if (context?.previousData) queryClient.setQueryData(context.queryKey, context.previousData);
      options?.onError?.(err, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      if (context?.queryKey) queryClient.invalidateQueries({ queryKey: context.queryKey });
      options?.onSettled?.(data, error, variables, context);
    },
  });
}
